import React, { useEffect, useRef, useState } from 'react';
import { Bookmark, Loader2, MoreVertical, PenTool, Trash2 } from 'lucide-react';
import { BookmarkNote } from './BookmarkNote';
import { cn } from '../../../lib/utils';
import { FormattedBookmark } from '../../../hooks/queries/bookmarkTypes';

interface BookmarkCardProps {
  bookmark: FormattedBookmark;
  onDelete: () => void;
}

export const BookmarkCard: React.FC<BookmarkCardProps> = ({ bookmark, onDelete }) => {
  const [showMenu, setShowMenu] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showNoteEditor, setShowNoteEditor] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const isLoading =
    bookmark.metadata.processingStatus === 'pending' ||
    bookmark.metadata.processingStatus === 'processing';

  const hasError = bookmark.metadata.processingStatus === 'failed';

  // Add click outside handler
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        showMenu &&
        menuRef.current &&
        buttonRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setShowMenu(false);
        setConfirmDelete(false);
      }
    }

    // Add event listener when the menu is shown
    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    // Clean up the event listener
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  const handleVisit = () => {
    window.open(bookmark.url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div
      onClick={handleVisit}
      className={cn(
        'relative group bg-white border rounded-xl shadow-sm overflow-hidden flex flex-col h-auto cursor-pointer',
        isLoading && 'opacity-80',
        hasError && 'border-red-200'
      )}
    >
      {/* Image section - fixed height */}
      <div
        className="h-32 bg-gray-100 flex items-center justify-center relative overflow-hidden"
        onClick={handleVisit}
      >
        {bookmark.imageUrl ? (
          <img
            src={bookmark.imageUrl}
            alt={bookmark.title || 'Website preview'}
            className="w-full h-full object-cover transition-transform group-hover:scale-105"
          />
        ) : (
          <div className="flex items-center justify-center w-full h-full bg-gray-200">
            {isLoading ? (
              <div className="animate-pulse flex flex-col items-center">
                <div className="h-12 w-12 bg-gray-300 rounded-full mb-2"></div>
                <div className="h-2 w-24 bg-gray-300 rounded"></div>
              </div>
            ) : (
              <Bookmark size={48} className="text-gray-400" />
            )}
          </div>
        )}

        {/* Loading/error overlay */}
        {(isLoading || hasError) && (
          <div
            className={cn(
              'absolute inset-0 flex items-center justify-center',
              isLoading ? 'bg-gray-800/20' : 'bg-red-500/10'
            )}
          >
            {isLoading && <Loader2 className="animate-spin text-blue-500" size={32} />}
            {hasError && (
              <div className="text-red-500 text-sm bg-white/80 px-2 py-1 rounded">
                Error loading bookmark
              </div>
            )}
          </div>
        )}
      </div>

      {/* Content section - takes up bottom half */}
      <div className="p-4 flex-1 flex flex-col w-full overflow-hidden">
        <h3 className="font-medium text-gray-900 line-clamp-1 mb-1">
          {bookmark.title || new URL(bookmark.url).hostname}
        </h3>

        {/* Bookmark Note - positioned between title and description */}
        <div className="w-full overflow-hidden">
          <BookmarkNote
            bookmark={bookmark}
            initialEditMode={showNoteEditor}
            onEditComplete={() => setShowNoteEditor(false)}
          />
        </div>

        <p className="text-gray-500 text-xs line-clamp-3 mb-2">
          {bookmark.description || bookmark.summary || 'No description available'}
        </p>

        <div className="mt-auto flex items-center">
          <span className="text-xs text-gray-400 truncate">{new URL(bookmark.url).hostname}</span>
        </div>
      </div>

      {/* Menu button and dropdown */}
      <div className="absolute top-2 right-2">
        <button
          ref={buttonRef}
          onClick={e => {
            e.stopPropagation(); // Prevent card click
            setShowMenu(!showMenu);
          }}
          className="p-1 bg-white/80 hover:bg-white rounded-full text-gray-600 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
        >
          <MoreVertical size={16} />
        </button>

        {showMenu && (
          <div
            ref={menuRef}
            onClick={e => e.stopPropagation()} // Prevent card click when clicking the menu
            className="absolute right-0 top-8 w-32 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10"
          >
            {!confirmDelete ? (
              <>
                <button
                  onClick={e => {
                    e.stopPropagation();
                    setShowMenu(false);
                    setShowNoteEditor(true);
                  }}
                  className="w-full text-left px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50 flex items-center gap-2"
                >
                  <PenTool size={14} />
                  <span>{bookmark.note ? 'Edit note' : 'Add note'}</span>
                </button>

                <button
                  onClick={e => {
                    e.stopPropagation();
                    setConfirmDelete(true);
                  }}
                  className="w-full text-left px-3 py-1.5 text-sm text-red-500 hover:bg-red-50 flex items-center gap-2"
                >
                  <Trash2 size={14} />
                  <span>Delete</span>
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={e => {
                    e.stopPropagation();
                    onDelete();
                    setShowMenu(false);
                  }}
                  className="w-full text-left px-3 py-1.5 text-sm text-red-500 hover:bg-red-50"
                >
                  Yes, delete
                </button>
                <button
                  onClick={e => {
                    e.stopPropagation();
                    setConfirmDelete(false);
                  }}
                  className="w-full text-left px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50"
                >
                  Cancel
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
